// Cash Request — weekly cash-planning proposal (mirrors Finance.API CashRequestDto / CashRequestInsert).
// The upstream step of the funding flow: an Approved Cash Request is later funded by a CashFunding.
import { FormControl } from '@angular/forms';
import { ApDocumentStatus, EntityKey, EntityKeyByReference } from '../ap-document/ap-document';
import { ApPaymentRequestStatus } from '../ap-payment-request/ap-payment-request';
import { ApPaymentStatus } from '../ap-payment/ap-payment';
import { Currency } from '../bank-account/bank-account';
import { ResponseTranslation } from '../Translations';
import { WithholdingCategory } from '../withholding-document/withholding-document';

export { Currency };
export type { EntityKey, EntityKeyByReference };

export enum CashRequestStatus {
  Draft = 1,
  Submitted = 2,
  Approved = 3,
  Cancelled = 4,
}

export interface GlAccountRef {
  reference?: string | null;
  number?: string | null;
}

/**
 * One thing planned against a line's GL account (#4863) — either a real document or a free entry the
 * requester typed. `sourceDocument === null` is what makes it manual.
 */
export interface CashRequestBreakdownEntry {
  entryId?: string;
  sourceDocument?: EntityKey | null;
  description?: string | null;
  /** Currency the source is denominated in — display only. */
  sourceCurrency: Currency;
  /** The amount in `sourceCurrency` — display only, so an HNL 250,000 invoice still reads as that. */
  sourceAmount: number;
  /** The entry's contribution in USD. The ONLY number the line and the request total sum. */
  functionalAmount: number;
}

/** One line of a Cash Request: ONE GL account, plus everything planned against it for the week. */
export interface CashRequestLine {
  lineId?: string;
  description?: string | null;
  glAccount?: GlAccountRef | null;
  /** Localized account name (#4863). Absent on lines planned before it was captured — fall back to number. */
  accountName?: ResponseTranslation | null;
  breakdown: CashRequestBreakdownEntry[];
  /** USD — derived server-side from the breakdown, never settable. */
  amount: number;
  /** USD subtotal of the document-backed entries. */
  documentTotal: number;
  /** USD subtotal of the manual entries. */
  manualTotal: number;
  /** Durable S3 key of the supporting document (set server-side on upload). */
  attachmentReference?: string | null;
  /** Presigned URL hydrated on read — used to open the attachment. */
  attachmentUrl?: string | null;
}

export interface CashRequest {
  id?: string | null;
  number?: string;
  weekStart: string;
  weekEnd: string;
  financialCompanyReference: string;
  currency: Currency;
  requestedBy?: string | null;
  status: CashRequestStatus;
  totalAmount: number;
  lines: CashRequestLine[];
  notes?: string | null;
  createdBy?: string;
  createdDate?: string;
  modifiedBy?: string;
  modifiedDate?: string;
}

/** A free entry the requester typed. Documents are NEVER supplied this way — see `CashRequestDocumentsUpdate`. */
export interface CashRequestManualEntryInsert {
  /** Present = update that entry, absent = add a new one. Any existing entry left out is deleted. */
  entryId?: string | null;
  description?: string | null;
  /** USD — a manual entry is dollars by definition. */
  amount: number;
}

/** Caller-supplied line: a GL account plus its manual entries. The amount is the sum of the breakdown. */
export interface CashRequestLineInsert {
  description?: string | null;
  glAccount: GlAccountRef;
  manualEntries: CashRequestManualEntryInsert[];
  attachmentReference?: string | null;
}

/**
 * What the document picker commits when it closes: only what CHANGED, never the full selection.
 *
 * Deliberately a delta — the picker filters, so a payload meaning "this is everything selected" would
 * silently unplan every document the active filter happened to hide.
 */
export interface CashRequestDocumentsUpdate {
  add: EntityKeyByReference[];
  remove: EntityKeyByReference[];
}

/** The manual section's Save for ONE line. Document entries on that line are untouched. */
export interface CashRequestManualEntriesUpdate {
  entries: CashRequestManualEntryInsert[];
}

export interface CashRequestInsert {
  financialCompanyReference: string;
  currency: Currency;
  weekStart: string;
  weekEnd?: string | null;
  notes?: string | null;
  lines: CashRequestLineInsert[];
}

/** A per-line description edit applied as part of a draft save. */
export interface CashRequestLineDescriptionUpdate {
  lineId: string;
  description?: string | null;
}

/** Editable fields of a Draft cash request: header (week, currency, notes) + per-line description edits. */
export interface CashRequestHeaderUpdate {
  currency: Currency;
  weekStart: string;
  weekEnd?: string | null;
  notes?: string | null;
  lineDescriptions: CashRequestLineDescriptionUpdate[];
}

/** A configurable Finance line-type from the catalog (scoped to CashRequest = entity code 51). */
export interface FinancialLineType {
  id?: string;
  entityDefinitionCode: number;
  code: string;
  name: string;
  defaultGlAccountReference?: string | null;
  defaultGlAccountNumber?: string | null;
  isActive: boolean;
}

/** GlobalEntityDefinition.CashRequest — the line-type catalog scope. */
export const CASH_REQUEST_ENTITY_CODE = 51;

/** GlobalEntityDefinition.ApPaymentRequest — one of the two things a cash request can plan. */
export const AP_PAYMENT_REQUEST_ENTITY_CODE = 50;

/** GlobalEntityDefinition.ApRetention — the code a WithholdingDocument travels under. */
export const WITHHOLDING_ENTITY_CODE = 60;

/** GlobalEntityDefinition.ApAdvanceRequest — an approved vendor advance planned on a cash request (#6323). */
export const AP_ADVANCE_REQUEST_ENTITY_CODE = 72;

/**
 * Header-only form. Lines are not typed here: they arrive from the document picker, and manual entries are
 * saved through their own per-line endpoint.
 *
 * There is no `weekEnd` control and no `currency` control. A cash request covers ONE week, so picking the
 * week is the whole input — the end is Monday + 6 and the API derives it. And a cash request is planned in
 * USD (every entry contributes its functional amount), so the currency is a fact, not a choice.
 */
export interface CashRequestForm {
  weekStart: FormControl<Date | null>;
  notes: FormControl<string | null>;
}

// ── Usage: what has actually been spent against the request (#4796) ───────────

/** What kind of document consumed the cash — they route to different screens and read differently. */
export enum CashRequestUsageSourceType {
  ApPayment = 1,
  WithholdingRemittance = 2,
}

/**
 * One document LINE that drew money from this cash request — an AP payment allocation or a withholding
 * remittance line. The line, never the document: one payment settles several invoices and one remittance
 * covers a month of retentions, and either can have been planned across different weeks.
 */
export interface CashRequestUsageLine {
  sourceType: CashRequestUsageSourceType;
  sourceReference: string;
  sourceNumber: string;
  /** Payment date, or the remittance's due date. */
  date: string;
  status: ApPaymentStatus;
  /** Currency of `appliedAmount` — the paying document's. */
  currency: Currency;
  providerName?: string | null;
  /** Null on a draft that has not chosen a bank account yet. */
  bankName?: string | null;
  bankAccountNumber?: string | null;
  /** The AP document: the invoice paid, or the invoice a retention was taken from. */
  documentReference?: string | null;
  documentNumber: string;
  /** The provider's own printed number (#4788), snapshotted on the allocation. Empty on a remittance line. */
  fiscalNumber: string;
  /** The Constancia remitted — remittance lines only, where the retention is the thing being paid. */
  retentionNumber?: string | null;
  /** Cash applied, in `currency`. Gross — before withholding. */
  appliedAmount: number;
  /** The same money in USD, converted at the source document's own booking rate — the only figure comparable
   *  to the plan, which is USD-only. */
  appliedFunctionalAmount: number;
}

/**
 * How much of the request has been spent, and on what. A read-side aggregate over the AP payment lines
 * tagged to it — nothing about consumption is stored on the request, so it cannot drift.
 */
export interface CashRequestUsage {
  cashRequestReference: string;
  number: string;
  status: CashRequestStatus;
  /** USD the request planned for — the denominator. */
  plannedAmount: number;
  /** USD drawn by ALL tagged lines — AP payments and withholding remittances, draft and posted alike. */
  appliedAmount: number;
  /** The posted share — money actually out of the bank. */
  postedAmount: number;
  /** The draft share — committed on paper, not yet paid. */
  draftAmount: number;
  /** Planned − applied. NEGATIVE means the week was overdrawn. */
  remainingAmount: number;
  /** Share of the plan consumed, 0–100 and uncapped above it when overdrawn. */
  appliedPercent: number;
  /** Distinct paying documents involved — the lines are per invoice. */
  paymentCount: number;
  lines: CashRequestUsageLine[];
}

// ── Document picker (#4864) ───────────────────────────────────────────────────

/** One GL account a candidate would plan against, and the share it would contribute there. */
export interface CashRequestCandidateAccount {
  reference: string;
  number?: string | null;
  name?: ResponseTranslation | null;
  /** The share in the candidate's own currency — display only. */
  sourceAmount: number;
  /** The share in USD — what this account's entry would actually contribute. */
  functionalAmount: number;
}

/**
 * Everything the picker needs about one thing a cash request could plan. Payment requests and withholdings
 * share this shape so both tabs render from ONE call and one row template.
 */
export interface CashRequestCandidate {
  /** What this row IS — handed straight back to plan it, so the kind is never re-derived. */
  sourceDocument: EntityKey;
  documentDate: string;
  /** Reference + code + legal name. Always present: only posted/approved sources are ever offered. */
  provider: { reference?: string | null; code?: string | null; name?: string | null };
  currency: Currency;
  /** CASH in the candidate's own currency — a payment request is net of any advance it earmarks (#4349). */
  sourceAmount: number;
  /** CASH in USD — what planning this would add to the request. Net of the advance for the same reason. */
  functionalAmount: number;
  /** More than one entry means the candidate spans several accounts and lands as one entry per account. */
  accounts: CashRequestCandidateAccount[];
  /** Already planned into the request being edited — shown checked, and unchecking unplans it. */
  alreadyInThisRequest: boolean;
  /** Planned into some OTHER cash request. Filtered out unless "include already planned" is on. */
  inAnotherCashRequest: boolean;
  /** Every cash request this candidate belongs to (#4798), so a row can name them. */
  cashRequestReferences: EntityKey[];
}

/** The AP document behind a payment-request row — a chip that opens the quick view (#4866). */
export interface CashRequestCandidateDocument {
  sourceDocument: EntityKey;
  /** The provider's own fiscal number (#4788) — what the paper invoice says. */
  fiscalNumber?: string | null;
  /** What the payment request pays on this document (its line's AmountToPay), not the document's total. */
  amount: number;
  /** The line's own currency — a mixed-currency request (#5135) doesn't share one across its documents. */
  currency: Currency;
}

export interface PaymentRequestCandidate extends CashRequestCandidate {
  status: ApPaymentRequestStatus;
  /**
   * The vendor advance (Anticipo) this request earmarks, ALREADY deducted from the amounts above (#4349).
   * Carried so the row can say why it asks for less than its invoices come to; stated in the same currency
   * as `sourceAmount`. A request covered in full comes to 0 cash and is still offered.
   */
  advanceAppliedAmount: number;
  requestedBy?: string | null;
  documents: CashRequestCandidateDocument[];
}

export interface WithholdingCandidate extends CashRequestCandidate {
  status: ApDocumentStatus;
  /** The Constancia number issued to the provider. */
  certificateNumber?: string | null;
  category: WithholdingCategory;
  invoiceReference?: EntityKey | null;
}

/** A purchase order an advance request is raised against. */
export interface CashRequestCandidatePurchaseOrder {
  reference: string;
  number?: string | null;
  /** The share of the advance requested against this PO, in the request's currency. */
  amount: number;
}

/**
 * An APPROVED vendor advance request (#6323). Lands as one entry on the "Anticipo a Proveedores" account.
 * Never offered once it is planned into another live cash request — the include toggle does not apply.
 */
export interface AdvanceRequestCandidate extends CashRequestCandidate {
  status: ApPaymentRequestStatus;
  requestedBy?: string | null;
  purchaseOrders: CashRequestCandidatePurchaseOrder[];
}

/** Every picker tab in one response. */
export interface CashRequestCandidates {
  paymentRequests: PaymentRequestCandidate[];
  withholdings: WithholdingCandidate[];
  advanceRequests: AdvanceRequestCandidate[];
}
